<script lang="ts">
  import Navbar from "$lib/components/Navbar.svelte";
  import TrialBanner from "$lib/components/TrialBanner.svelte";
  import { auth } from "$lib/stores/auth.svelte";
  import { navigate } from "$lib/router.svelte";
  import { api } from "$lib/api/client";
  import { toasts } from "$lib/stores/toasts.svelte";
  import { onMount } from "svelte";

  interface Plan {
    id: string; name: string; price_brl_month: number; price_brl_year: number;
    members: number | null; tx_per_month: number | null; storage_gb: number;
    rules: number | null; highlights: string[];
  }

  interface CheckoutOut {
    intent_id: string; plan: string; cycle: string;
    status: string; expires_at: string; pix: any; message: string;
  }

  let plans = $state<Plan[]>([]);
  let cycle = $state<"month" | "year">("year");
  let loading = $state(true);
  let processing = $state<string | null>(null);
  let pixOpen = $state(false);
  let checkout_ = $state<CheckoutOut | null>(null);
  let pollTimer = $state<ReturnType<typeof setInterval> | null>(null);
  let paymentConfirmed = $state(false);

  let billing = $derived(auth.billing);
  let isExpired = $derived(billing?.plan === "expired");
  let daysLeft = $derived(billing?.days_left ?? 0);
  let persona = $derived(auth.user?.persona ?? "personal");

  onMount(async () => {
    try { plans = await api<Plan[]>("/billing/plans"); }
    catch { toasts.error("Erro ao carregar planos"); }
    finally { loading = false; }
  });

  function fmt(n: number) {
    return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  }

  function monthlyFmt(p: Plan) {
    const n = cycle === "year" ? p.price_brl_year / 12 : p.price_brl_month;
    return fmt(n);
  }

  function saving(p: Plan) {
    return fmt(p.price_brl_month * 12 - p.price_brl_year);
  }

  async function checkout(planId: string) {
    processing = planId;
    try {
      const r = await api<CheckoutOut>("/billing/checkout", {
        method: "POST",
        body: JSON.stringify({ plan: planId, cycle }),
      });
      checkout_ = r;
      pixOpen = true;
      startPolling(r.intent_id);
    } catch (e: any) {
      toasts.error("Erro no checkout", e.message);
    } finally {
      processing = null;
    }
  }

  function startPolling(intentId: string) {
    if (pollTimer) clearInterval(pollTimer);
    pollTimer = setInterval(async () => {
      try {
        const r = await api<CheckoutOut>(`/billing/intent/${intentId}`);
        if (r.status === "paid") {
          clearInterval(pollTimer!);
          pollTimer = null;
          paymentConfirmed = true;
          await auth.bootstrap(); // atualiza billing status
        }
      } catch { /* ignora erros de polling */ }
    }, 30_000);
  }

  function closeModal() {
    pixOpen = false;
    if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
    if (paymentConfirmed) navigate("/dashboard");
  }

  async function copyPayload() {
    if (!checkout_?.pix) return;
    try {
      await navigator.clipboard.writeText(checkout_.pix.payload);
      toasts.success("Código PIX copiado!");
    } catch {
      toasts.warn("Não foi possível copiar", "Selecione e copie manualmente.");
    }
  }

  const planMeta: Record<string, { label: string; useCase: string; testimonial: { text: string; author: string } }> = {
    pro: {
      label: "Pro",
      useCase: "Para quem cuida das finanças pessoais e da família",
      testimonial: {
        text: "Organizei as finanças da família em 2 dias. O Telegram mudou tudo — registro um gasto pelo celular em segundos.",
        author: "Lucas M., 34 anos",
      },
    },
    business: {
      label: "Business",
      useCase: "Para MEI, autônomo e pequenas empresas",
      testimonial: {
        text: "Substituí 3 planilhas por uma só ferramenta. Os relatórios me salvam nas reuniões com o contador.",
        author: "Beatriz C., empreendedora",
      },
    },
  };

  const featureRows = [
    { label: "Workspaces", pro: "3", biz: "10" },
    { label: "Membros por workspace", pro: "Até 8", biz: "Até 50" },
    { label: "Transações", pro: "Ilimitadas", biz: "Ilimitadas" },
    { label: "Telegram: entrada por voz/texto", pro: true, biz: true },
    { label: "Recorrências e parcelas", pro: true, biz: true },
    { label: "Metas e orçamentos", pro: true, biz: true },
    { label: "IA: classifica e lê recibos", pro: true, biz: true },
    { label: "Exportação CSV / OFX", pro: true, biz: true },
    { label: "Exportação PDF / XLSX", pro: true, biz: true },
    { label: "Backup OneDrive", pro: true, biz: true },
    { label: "Regras automáticas", pro: "50 regras", biz: "Ilimitadas" },
    { label: "Papéis de acesso (owner/admin/viewer)", pro: true, biz: true },
    { label: "Audit log de alterações", pro: false, biz: true },
    { label: "Webhooks", pro: false, biz: true },
    { label: "API pública", pro: false, biz: true },
    { label: "Suporte prioritário", pro: false, biz: true },
  ];
</script>

<Navbar />
<TrialBanner />

<main class="max-w-5xl mx-auto px-4 py-10">

  <!-- Hero -->
  <div class="text-center mb-10">
    {#if isExpired}
      <div class="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-950 border border-red-800 text-red-300 text-sm mb-4">
        ⛔ Acesso expirado — seus dados estão seguros, aguardando você
      </div>
      <h1 class="text-3xl font-bold">Reative o TáKite</h1>
      <p class="text-muted mt-2">Escolha um plano e retome em segundos. Nenhum dado foi perdido.</p>
    {:else}
      <div class="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-950 border border-indigo-800 text-indigo-300 text-sm mb-4">
        🎉 {daysLeft} dia{daysLeft !== 1 ? 's' : ''} de trial restante{daysLeft !== 1 ? 's' : ''}
      </div>
      <h1 class="text-3xl font-bold">Continue sem interrupções</h1>
      <p class="text-muted mt-2">Assine agora e nunca perca um lançamento.</p>
    {/if}

    <!-- Toggle ciclo -->
    <div class="inline-flex items-center gap-1 mt-6 p-1 rounded-full border" style="border-color: var(--border)">
      <button
        class="px-4 py-1.5 rounded-full text-sm font-medium transition"
        class:bg-brand={cycle === "month"} class:text-white={cycle === "month"}
        onclick={() => (cycle = "month")}
      >Mensal</button>
      <button
        class="px-4 py-1.5 rounded-full text-sm font-medium transition relative"
        class:bg-brand={cycle === "year"} class:text-white={cycle === "year"}
        onclick={() => (cycle = "year")}
      >
        Anual
        <span class="ml-1.5 text-xs px-1.5 py-0.5 rounded bg-green-600 text-white font-semibold">2 meses grátis</span>
      </button>
    </div>
  </div>

  <!-- Cards de planos -->
  {#if loading}
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
      {#each [1,2] as _}<div class="skeleton h-96 rounded-2xl"></div>{/each}
    </div>
  {:else}
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
      {#each plans.filter(p => p.id !== "free") as p}
        {@const meta = planMeta[p.id]}
        {@const isRec = p.id === "pro"}
        <div
          class="relative rounded-2xl border flex flex-col overflow-hidden
            {isRec ? 'border-indigo-500 shadow-xl shadow-indigo-900/30' : ''}"
          style="background: var(--card-bg)"
        >
          {#if isRec}
            <div class="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-indigo-500 to-violet-500"></div>
            <span class="absolute top-4 right-4 px-2 py-0.5 rounded-full bg-indigo-600 text-white text-xs font-semibold">
              Recomendado
            </span>
          {/if}

          <div class="p-6 pb-0">
            <h2 class="text-xl font-bold">{meta?.label ?? p.name}</h2>
            <p class="text-xs text-muted mt-1">{meta?.useCase ?? ""}</p>

            <div class="mt-4 flex items-end gap-1">
              <span class="text-4xl font-extrabold">{monthlyFmt(p)}</span>
              <span class="text-sm text-muted pb-1.5">/mês</span>
            </div>
            {#if cycle === "year"}
              <p class="text-xs mt-1">
                <span class="text-green-400 font-semibold">{fmt(p.price_brl_year)}/ano</span>
                <span class="text-muted"> — você economiza {saving(p)}</span>
              </p>
            {:else}
              <p class="text-xs text-muted mt-1">
                ou {fmt(p.price_brl_year)}/ano (economize {saving(p)})
              </p>
            {/if}
          </div>

          <ul class="px-6 mt-5 text-sm space-y-2 text-muted flex-1">
            {#each p.highlights as h}
              <li class="flex items-start gap-2">
                <span class="text-green-400 mt-0.5 shrink-0">✓</span>
                <span>{h}</span>
              </li>
            {/each}
          </ul>

          <!-- Depoimento -->
          {#if meta?.testimonial}
            <div class="mx-6 mt-5 p-3 rounded-xl text-xs italic text-muted" style="background: var(--bg)">
              <p>"{meta.testimonial.text}"</p>
              <p class="mt-1 font-semibold not-italic text-foreground">— {meta.testimonial.author}</p>
            </div>
          {/if}

          <div class="p-6 pt-5">
            <button
              disabled={!!processing}
              onclick={() => checkout(p.id)}
              class="w-full py-3 rounded-xl font-semibold text-sm transition
                {isRec
                  ? 'bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 text-white shadow-lg shadow-indigo-900/40'
                  : 'border hover:bg-white/5 text-foreground'}"
              style="{isRec ? '' : 'border-color: var(--border)'}"
            >
              {processing === p.id ? "Aguarde…" : `Assinar ${meta?.label ?? p.name} via PIX →`}
            </button>
          </div>
        </div>
      {/each}
    </div>
  {/if}

  <!-- Comparativo de features -->
  <div class="mt-14">
    <h2 class="text-xl font-bold text-center mb-6">Comparativo completo</h2>
    <div class="rounded-2xl border overflow-hidden" style="border-color: var(--border)">
      <table class="w-full text-sm">
        <thead>
          <tr style="background: var(--card-bg)">
            <th class="text-left px-5 py-3 font-semibold text-muted">Funcionalidade</th>
            <th class="px-5 py-3 text-center font-semibold text-indigo-400">Pro</th>
            <th class="px-5 py-3 text-center font-semibold text-violet-400">Business</th>
          </tr>
        </thead>
        <tbody>
          {#each featureRows as row, i}
            <tr class="{i % 2 === 0 ? '' : ''}" style="border-top: 1px solid var(--border)">
              <td class="px-5 py-2.5 text-muted">{row.label}</td>
              <td class="px-5 py-2.5 text-center">
                {#if row.pro === true}
                  <span class="text-green-400">✓</span>
                {:else if row.pro === false}
                  <span class="text-muted opacity-40">—</span>
                {:else}
                  <span class="text-foreground text-xs">{row.pro}</span>
                {/if}
              </td>
              <td class="px-5 py-2.5 text-center">
                {#if row.biz === true}
                  <span class="text-green-400">✓</span>
                {:else if row.biz === false}
                  <span class="text-muted opacity-40">—</span>
                {:else}
                  <span class="text-foreground text-xs">{row.biz}</span>
                {/if}
              </td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
  </div>

  <!-- Trust footer -->
  <div class="mt-10 flex flex-wrap justify-center gap-6 text-xs text-muted">
    <span>🔒 Dados criptografados e seus</span>
    <span>⚡ Ativação em minutos após o Pix</span>
    <span>🚫 Sem fidelidade — cancele quando quiser</span>
    <span>📞 Suporte humano por e-mail</span>
  </div>
</main>

<!-- Modal PIX -->
{#if pixOpen && checkout_?.pix}
  {@const x = checkout_.pix}
  <div class="fixed inset-0 z-50 flex items-center justify-center p-4" style="background: rgba(0,0,0,.7)">
    <div class="w-full max-w-sm rounded-2xl p-6 space-y-4 relative" style="background: var(--card-bg)">
      <button
        onclick={closeModal}
        class="absolute top-4 right-4 text-muted hover:text-foreground text-xl leading-none"
      >✕</button>

      {#if paymentConfirmed}
        <div class="text-center space-y-3 py-4">
          <div class="text-5xl">🎉</div>
          <h2 class="text-xl font-bold text-green-400">Pagamento confirmado!</h2>
          <p class="text-muted text-sm">Seu plano está ativo. Redirecionando…</p>
        </div>
      {:else}
        <div class="text-center">
          <h2 class="text-lg font-bold">Pagamento via PIX</h2>
          <p class="text-2xl font-extrabold text-brand mt-1">{fmt(x.amount)}</p>
          <p class="text-xs text-muted">
            Plano {checkout_?.plan?.toUpperCase()} · {checkout_?.cycle === "year" ? "anual" : "mensal"}
          </p>
        </div>

        <div class="flex justify-center">
          <img src={x.qr_base64} alt="QR Code PIX" class="w-52 h-52 bg-white p-2 rounded-xl" />
        </div>

        <div>
          <p class="text-xs text-muted mb-1">PIX Copia-e-cola</p>
          <textarea readonly class="input font-mono text-xs w-full" rows="3" value={x.payload}></textarea>
          <button class="btn-secondary w-full mt-2 text-sm" onclick={copyPayload}>
            📋 Copiar código PIX
          </button>
        </div>

        <div class="rounded-xl p-3 text-xs space-y-1" style="background: var(--bg)">
          <div><strong>Beneficiário:</strong> {x.merchant}</div>
          {#if x.payee_doc}<div><strong>CNPJ:</strong> {x.payee_doc}</div>{/if}
          {#if x.bank}<div><strong>Banco:</strong> {x.bank}</div>{/if}
          <div><strong>Chave {x.key_type.toUpperCase()}:</strong> {x.key}</div>
          <div><strong>ID:</strong> {x.txid}</div>
        </div>

        <div class="flex items-center gap-2 text-xs text-muted">
          <span class="inline-block w-2 h-2 rounded-full bg-amber-400 animate-pulse shrink-0"></span>
          Aguardando confirmação do pagamento… (atualiza automaticamente)
        </div>

        <p class="text-xs text-muted text-center">
          Após pagar, envie o comprovante para <strong>contato@takite.com</strong>
          com o código <strong>{x.txid}</strong> — plano liberado em até 1h.
        </p>
      {/if}
    </div>
  </div>
{/if}

